import { Box, Button, Chip, Container, IconButton, Stack, Typography, useTheme, alpha } from "@mui/material";
import { ArrowBack, AccessTime, CalendarMonth, ChevronRight, Favorite, FavoriteBorder, Lock } from "@mui/icons-material";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { IPublicEventDetails } from "../../../interfaces/IPublicEventDetails";
import { paths } from "../../../routes/paths";
import { formatEventTimeRange } from "../../../utils/eventTimeZones";

interface EventBannerProps {
    event: IPublicEventDetails;
    imageUrl: string | null;
    onToggleFavourite?: () => void;
}

function formatDateRange(startDate: string, endDate: string, isSingleDay?: boolean): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    // Trust the backend-computed flag rather than comparing calendar dates, which can
    // disagree with reality once a UTC timestamp is converted to local display time.
    if (isSingleDay) {
        return start.format("MMM D, YYYY");
    }
    if (start.year() !== end.year()) {
        return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
    } else if (start.month() !== end.month()) {
        return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    } else if (start.date() !== end.date()) {
        return `${start.format("MMM D")}-${end.format("D, YYYY")}`;
    } else {
        return start.format("MMM D, YYYY");
    }
}

export default function EventBanner({ event, imageUrl, onToggleFavourite }: EventBannerProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const eventTimeRange = formatEventTimeRange(event.startUtc, event.endUtc, event.timeZoneId, event.eventDays);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: { xs: 280, sm: 320, md: 400 },
                backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${imageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                alignItems: "center",
                mb: { xs: 3, md: 4 },
                overflow: "hidden",
            }}
        >
            {/* Favorite Button - Repositioned for mobile */}
            <IconButton
                onClick={onToggleFavourite}
                sx={{
                    position: "absolute",
                    top: { xs: 12, md: 16 },
                    right: { xs: 12, md: 24 },
                    bgcolor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.3s ease',
                    zIndex: 2,
                    "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 1)",
                        transform: "scale(1.15)",
                        boxShadow: '0 6px 20px rgba(73, 49, 147, 0.3)',
                    },
                }}
                aria-label={event.isFavourite ? "Remove from favourites" : "Add to favourites"}
            >
                {event.isFavourite ? (
                    <Favorite sx={{ color: "#d32f2f" }} />
                ) : (
                    <FavoriteBorder sx={{ color: "#493193" }} />
                )}
            </IconButton>

            {/* Browse Events Button - Better mobile positioning */}
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate(paths.public.searchEvents)}
                aria-label="Browse Events"
                variant="contained"
                sx={{
                    position: "absolute",
                    top: { xs: 12, md: 24 },
                    left: { xs: 12, md: 24 },
                    zIndex: 2,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: '50px',
                    bgcolor: '#493193',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                    textTransform: "none",
                    transition: 'all 0.3s ease',
                    "&:hover": {
                        bgcolor: '#3a2676',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                    },
                    "&:focus-visible": {
                        outline: "2px solid white",
                        outlineOffset: "2px",
                    },
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Browse Events
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Back
                </Box>
            </Button>

            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                <Box sx={{ maxWidth: 800, width: "100%", pt: { xs: 8, sm: 0 } }}>
                    {/* Tags */}
                    <Stack 
                        direction="row" 
                        spacing={1} 
                        sx={{ 
                            mb: 2, 
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        {!event.isEventPublic && (
                            <Chip
                                icon={<Lock sx={{ fontSize: '0.875rem !important', color: 'white !important' }} />}
                                label="Private Event"
                                size="small"
                                sx={{
                                    borderRadius: '50px',
                                    bgcolor: 'rgba(69, 69, 69, 0.85)',
                                    color: 'white',
                                    fontWeight: 700,
                                    px: { xs: 0.5, sm: 1 },
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                }}
                            />
                        )}
                        {event.isInviteOnly && (
                            <Chip
                                icon={<Lock sx={{ fontSize: '0.875rem !important', color: 'white !important' }} />}
                                label="Invite Only"
                                size="small"
                                sx={{
                                    borderRadius: '50px',
                                    bgcolor: 'rgba(237, 108, 2, 0.9)',
                                    color: 'white',
                                    fontWeight: 700,
                                    px: { xs: 0.5, sm: 1 },
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                }}
                            />
                        )}
                        {event.tags.map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                    borderRadius: '50px',
                                    bgcolor: 'rgba(73, 49, 147, 0.9)',
                                    color: 'white',
                                    fontWeight: 600,
                                    px: { xs: 0.5, sm: 1 },
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    '&:hover': {
                                        bgcolor: 'rgba(58, 38, 118, 1)',
                                    },
                                }}
                            />
                        ))}
                    </Stack>

                    {/* Event Title */}
                    <Typography
                        variant="h2"
                        component="h1"
                        color="white"
                        gutterBottom
                        sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "1.5rem", sm: "2rem", md: "3rem" },
                            lineHeight: 1.1,
                            mb: { xs: 1.5, md: 2 },
                        }}
                    >
                        {event.title}
                    </Typography>

                    {/* Category and Subcategory */}
                    {(event.categoryName || event.subCategoryName) && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                gap: 0.5,
                                flexWrap: "wrap",
                                pb: { xs: 1.5, md: 2 },
                            }}
                        >
                            {event.categoryName && (
                                <Chip
                                    label={event.categoryName}
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(255, 255, 255, 0.25)",
                                        backdropFilter: "blur(10px)",
                                        color: "white",
                                        fontWeight: 600,
                                        border: "1px solid rgba(255, 255, 255, 0.4)",
                                        borderRadius: '50px',
                                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    }}
                                />
                            )}
                            {event.categoryName && event.subCategoryName && (
                                <ChevronRight sx={{ color: "rgba(255, 255, 255, 0.8)", fontSize: { xs: 18, sm: 20 } }} />
                            )}
                            {event.subCategoryName && (
                                <Chip
                                    label={event.subCategoryName}
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(255, 255, 255, 0.25)",
                                        backdropFilter: "blur(10px)",
                                        color: "white",
                                        fontWeight: 600,
                                        border: "1px solid rgba(255, 255, 255, 0.4)",
                                        borderRadius: '50px',
                                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Date - Better mobile layout */}
                    <Stack 
                        direction="row" 
                        spacing={{ xs: 1.5, sm: 3 }} 
                        sx={{ 
                            color: "white",
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
                            <CalendarMonth sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {formatDateRange(event.startDate.toString(), event.endDate.toString(), event.isSingleDay)}
                            </Typography>
                        </Box>
                        {eventTimeRange && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
                                <AccessTime sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    {eventTimeRange}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}